import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Bot,
  Database,
  BookOpen,
  FlaskConical,
  GitBranch,
  ArrowRight,
  Lock,
  Sparkles,
  Brain,
  Wifi,
  WifiOff,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ShieldCheck,
  BookMarked,
  Pencil,
  Check,
  X,
  Settings,
  Users,
} from "lucide-react";
import { AGENTS, type AgentDefinition } from "../agents";
import { apiv2, type ProjectOut } from "../apiv2";
import { useAuth } from "../auth";
import GreenTendrils from "./GreenTendrils";

interface Props {
  projectId: string;
  projectName?: string;
  onSelectAgent: (agentId: string) => void;
  onBack: () => void;
  onSetup?: () => void;
}

// Permission codename(s) each active agent's own API checks (any one grants
// access) -- kept here rather than in agents.ts since it mirrors backend
// require_permission() gates, not agent metadata. See each agent's router
// for the authoritative codename(s):
// data_profiler/agent_endpoints_router.py, data_guardian/masking_agent_endpoints_router.py,
// data_converter/router.py, data_semantic/agent_endpoints_router.py,
// metadata_curator/agent_endpoints_router.py, backlog/router.py,
// data_validation/router.py, mdm/api/mdm_routes.py, pipeline_engineer/agent_router.py,
// conversational_ai/router.py.
// agents.ts's `color` field is a semantic placeholder string ("brand",
// "accent-purple", "accent-cyan", "accent-amber", "accent-teal") -- not a
// literal CSS color. Every per-agent accent usage below (icon background,
// glow, active badge, workflow-step chips, CTA gradient) builds a string
// like `${color}15` / `${color}CC`, which only produces valid CSS when
// `color` is itself a 6-digit hex string. Passing the semantic name straight
// through silently produced invalid CSS values (e.g. "accent-purple15") for
// every single agent tile on the Home screen -- found while investigating
// docs/architecture/conversational-ai-phase2-rag.md §7.2. This map is the
// fix: resolve the semantic name to its real hex value once, here, rather
// than at each of the dozen use sites below.
const AGENT_ACCENT_HEX: Record<string, string> = {
  brand: "#86BC25", // --brand-500 (ztd-design-system.css) -- Deloitte green
  "accent-purple": "#8B5CF6", // --ai-generated -- "anything AI-generated"
  "accent-cyan": "#06B6D4", // Tailwind cyan-500 (already used elsewhere in this app)
  "accent-amber": "#F59E0B", // --warning
  "accent-teal": "#14B8A6", // Tailwind teal-500
};

function resolveAgentAccentColor(color: string): string {
  return AGENT_ACCENT_HEX[color] ?? AGENT_ACCENT_HEX.brand;
}

const AGENT_REQUIRED_PERMISSIONS: Record<string, string[]> = {
  "data-profiler": ["profiler:execute"],
  "data-guardian": ["profiler:execute"],
  "data-converter": ["converter:execute"],
  "semantic-intelligence": ["semantic:execute"],
  "metadata-curator": ["metadata:execute"],
  "backlog-architect": ["backlog:execute"],
  validation: ["validation:execute"],
  "pipeline-engineer": ["pipeline_engineer:read"],
  "mdm-agent": ["mdm:execute"],
  "conversational-ai": ["conversational_ai:execute"],
};

function hasAgentAccess(agentId: string, permissions: string[]): boolean {
  if (permissions.includes("*") || permissions.includes("admin:tenant"))
    return true;
  const required = AGENT_REQUIRED_PERMISSIONS[agentId];
  if (!required) return true; // no known gate -- don't lock out agents we haven't mapped
  return required.some((p) => permissions.includes(p));
}

const ICON_MAP: Record<string, typeof Database> = {
  Database,
  BookOpen,
  FlaskConical,
  GitBranch,
  ShieldCheck,
  BookMarked,
  Users,
};

/* -- Persistence for agent order -- */
const ORDER_KEY = "rgx-agent-order";

function loadOrder(): string[] | null {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveOrder(ids: string[]) {
  localStorage.setItem(ORDER_KEY, JSON.stringify(ids));
}

function getOrderedAgents(agents: AgentDefinition[]): AgentDefinition[] {
  const saved = loadOrder();
  if (!saved) return [...agents];
  const map = new Map(agents.map((a) => [a.id, a]));
  const ordered: AgentDefinition[] = [];
  for (const id of saved) {
    const a = map.get(id);
    if (a) {
      ordered.push(a);
      map.delete(id);
    }
  }
  // Append any new agents not in saved order
  map.forEach((a) => ordered.push(a));
  return ordered;
}

export default function AgentCatalog({
  projectId,
  projectName,
  onSelectAgent,
  onBack,
  onSetup,
}: Props) {
  const { user } = useAuth();
  const permissions = user?.permissions ?? [];
  const [orderedAgents, setOrderedAgents] = useState<AgentDefinition[]>(() =>
    getOrderedAgents(AGENTS),
  );
  const [llmOnline, setLlmOnline] = useState<boolean | null>(null); // null = loading
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // Project data for inline editing
  const [project, setProject] = useState<ProjectOut | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Fetch project details on mount
  useEffect(() => {
    apiv2
      .getProject(projectId)
      .then((p) => {
        setProject(p);
        setEditName(p.name);
        setEditDesc(p.description || "");
      })
      .catch(() => {});
  }, [projectId]);

  const handleSaveProjectInfo = async () => {
    if (!editName.trim()) return;
    setEditSaving(true);
    try {
      const updated = await apiv2.updateProject(projectId, {
        name: editName.trim(),
        description: editDesc.trim(),
      });
      setProject(updated);
      setEditing(false);
    } catch {
      /* keep editing open */
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (project) {
      setEditName(project.name);
      setEditDesc(project.description || "");
    }
    setEditing(false);
  };

  // Check LLM status on mount (use v2 endpoint that reads global config)
  useEffect(() => {
    apiv2
      .getLLMStatus()
      .then((r) => setLlmOnline(r.online))
      .catch(() => setLlmOnline(false));
  }, []);

  /* ── Drag & drop reorder ── */
  const handleDragStart = useCallback((idx: number) => {
    setDragIdx(idx);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  }, []);

  const handleDrop = useCallback(
    (idx: number) => {
      if (dragIdx === null || dragIdx === idx) {
        setDragIdx(null);
        setDragOverIdx(null);
        return;
      }
      setOrderedAgents((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragIdx, 1);
        next.splice(idx, 0, moved);
        saveOrder(next.map((a) => a.id));
        return next;
      });
      setDragIdx(null);
      setDragOverIdx(null);
    },
    [dragIdx],
  );

  const handleDragEnd = useCallback(() => {
    setDragIdx(null);
    setDragOverIdx(null);
  }, []);

  /* ── Arrow-based reorder ── */
  const moveAgent = useCallback((idx: number, dir: -1 | 1) => {
    setOrderedAgents((prev) => {
      const target = idx + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      saveOrder(next.map((a) => a.id));
      return next;
    });
  }, []);

  return (
    <div className="overflow-y-auto custom-scrollbar h-full bg-surface-500 relative">
      {/* Background effects — matches LandingPage */}
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
      <GreenTendrils />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-10 space-y-10">
        {/* Header */}
        <div className="flex items-center gap-5 flex-wrap animate-fade-in">
          <button
            onClick={onBack}
            className="p-2.5 rounded-xl border border-white/8 hover:border-brand-500/30 transition-all duration-300 hover:bg-brand-500/5"
            style={{ background: "rgba(28,28,42,0.5)" }}
          >
            <ArrowLeft size={18} className="text-white/50" />
          </button>
          <div className="flex-1 min-w-0">
            {/* Editable project title */}
            {editing ? (
              <div className="space-y-3 mb-3">
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full text-2xl font-bold bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 transition"
                  placeholder="Project name"
                  autoFocus
                />
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={2}
                  className="w-full text-sm bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white/70 placeholder-white/20 focus:outline-none focus:border-brand-500/50 transition resize-none"
                  placeholder="Project description (optional)"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveProjectInfo}
                    disabled={editSaving || !editName.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-500/20 text-brand-400 border border-brand-500/30 hover:bg-brand-500/30 transition disabled:opacity-40"
                  >
                    <Check size={12} /> {editSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/40 border border-white/10 hover:bg-white/5 transition"
                  >
                    <X size={12} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="group/title mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-white">
                    {project?.name ?? projectName ?? projectId}
                  </span>
                  <button
                    onClick={() => setEditing(true)}
                    className="p-1.5 rounded-lg text-white/20 hover:text-brand-400 hover:bg-white/5 transition opacity-0 group-hover/title:opacity-100"
                    title="Edit project details"
                  >
                    <Pencil size={14} />
                  </button>
                  {onSetup && (
                    // Discoverability fix (Apr 2026): the Settings entry point
                    // used to be a 14-px icon at opacity-0 until you hovered
                    // the title. Per-project LLM configuration lives behind
                    // it, and users couldn't find it. Now: persistent button
                    // with a label, plus an inline LLM badge so the active
                    // provider/model is visible without clicking through.
                    <button
                      onClick={onSetup}
                      className="ml-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white/70 border border-white/10 bg-white/5 hover:bg-white/10 hover:text-white transition"
                      title="Project settings — including per-project LLM provider, model, and API key"
                    >
                      <Settings size={14} />
                      Settings
                      {project?.llm_config && (
                        <span
                          className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider font-semibold bg-brand-500/15 text-brand-300 border border-brand-500/30"
                          title={`LLM: ${project.llm_config.provider} · ${project.llm_config.model_name}`}
                        >
                          LLM: {project.llm_config.provider}
                        </span>
                      )}
                      {!project?.llm_config && (
                        <span
                          className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider font-semibold bg-white/5 text-white/50 border border-white/10"
                          title="Falls back to global / environment LLM config"
                        >
                          LLM: default
                        </span>
                      )}
                    </button>
                  )}
                </div>
                {project?.description && (
                  <p className="text-xs text-white/30 mt-1 max-w-xl">
                    {project.description}
                  </p>
                )}
              </div>
            )}

            <h1 className="text-4xl md:text-5xl font-black tracking-tight">
              <span className="gradient-text-green">Agent</span>
              <span className="text-white/40 font-extralight"> Catalog</span>
            </h1>
            <p className="text-sm text-white/40 mt-2">
              Select an AI agent to work with in this project
            </p>
          </div>

          {/* LLM status indicator */}
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/5"
            style={{ background: "rgba(28,28,42,0.6)" }}
          >
            <Brain size={14} className="text-purple-400" />
            <span className="text-[11px] text-white/65 font-medium">LLM</span>
            {llmOnline === null ? (
              <span className="text-[11px] text-white/50">checking...</span>
            ) : llmOnline ? (
              <span className="flex items-center gap-1.5 text-[11px] font-bold text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Online
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-[11px] font-bold text-red-400/70">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400/70" />
                Offline
              </span>
            )}
          </div>
        </div>

        {/* Agent Cards — horizontal scroll */}
        <div className="overflow-x-auto pb-4 custom-scrollbar-x">
          <div
            className="flex gap-6 stagger-in"
            style={{ minWidth: "max-content" }}
          >
            {orderedAgents.map((agent, idx) => (
              <div
                key={agent.id}
                className={`flex-shrink-0 w-[340px] transition-all duration-200 ${
                  dragIdx === idx ? "opacity-40 scale-95" : ""
                } ${dragOverIdx === idx && dragIdx !== idx ? "ring-2 ring-brand-500/40 rounded-2xl" : ""}`}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={() => handleDrop(idx)}
                onDragEnd={handleDragEnd}
              >
                <AgentCard
                  agent={agent}
                  onSelect={() => onSelectAgent(agent.id)}
                  llmOnline={llmOnline}
                  index={idx}
                  total={orderedAgents.length}
                  onMove={moveAgent}
                  hasAccess={hasAgentAccess(agent.id, permissions)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Info bar */}
        <div className="glass-card rounded-2xl p-4 flex items-center gap-3 animate-slide-up">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(134,188,37,0.12)" }}
          >
            <Sparkles size={14} style={{ color: "#86BC25" }} />
          </div>
          <p className="text-xs text-white/40 leading-relaxed">
            Each agent has specialized expertise and produces different
            deliverables.
            <Brain size={10} className="inline mx-1 text-purple-400" />
            indicates LLM-powered agents. Drag cards or use arrows to reorder.
          </p>
        </div>
      </div>
    </div>
  );
}

function AgentCard({
  agent,
  onSelect,
  llmOnline,
  index,
  total,
  onMove,
  hasAccess,
}: {
  agent: AgentDefinition;
  onSelect: () => void;
  llmOnline: boolean | null;
  index: number;
  total: number;
  onMove: (idx: number, dir: -1 | 1) => void;
  hasAccess: boolean;
}) {
  const isComingSoon = agent.status !== "active";
  const isRestricted = !isComingSoon && !hasAccess;
  const isActive = agent.status === "active" && hasAccess;
  const Icon = ICON_MAP[agent.icon] || Bot;
  const color = resolveAgentAccentColor(agent.color);

  return (
    <div
      onClick={isActive ? onSelect : undefined}
      className={`glass-hero-card rounded-2xl p-6 group h-full relative transition-all duration-300 ${
        isActive ? "cursor-pointer hover:scale-[1.02]" : "cursor-default"
      }`}
      style={
        isActive
          ? undefined
          : { borderColor: "rgba(255,255,255,0.12)", opacity: 0.7 }
      }
    >
      {/* Coming Soon overlay */}
      {isComingSoon && (
        <div
          className="absolute inset-0 z-20 rounded-2xl flex flex-col items-center justify-center"
          style={{
            background: "rgba(14,14,24,0.55)",
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            className="px-5 py-2.5 rounded-xl border border-white/15 text-sm font-bold text-white/60 flex items-center gap-2"
            style={{ background: "rgba(28,28,42,0.8)" }}
          >
            <Lock size={14} className="text-white/40" />
            Coming Soon
          </div>
          <p className="text-[11px] text-white/30 mt-2">Under development</p>
        </div>
      )}
      {/* Restricted overlay — agent is live, but this account isn't permitted to use it */}
      {isRestricted && (
        <div
          className="absolute inset-0 z-20 rounded-2xl flex flex-col items-center justify-center"
          style={{
            background: "rgba(14,14,24,0.55)",
            backdropFilter: "blur(2px)",
          }}
        >
          <div
            className="px-5 py-2.5 rounded-xl border border-white/15 text-sm font-bold text-white/60 flex items-center gap-2"
            style={{ background: "rgba(28,28,42,0.8)" }}
          >
            <Lock size={14} className="text-white/40" />
            Restricted
          </div>
          <p className="text-[11px] text-white/30 mt-2">
            Not enabled for your account
          </p>
        </div>
      )}
      {/* Drag handle + reorder arrows */}
      <div
        className="absolute top-3 left-3 flex flex-col items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMove(index, -1);
          }}
          disabled={index === 0}
          className="p-0.5 rounded hover:bg-white/10 text-white/20 hover:text-white/60 disabled:opacity-0 transition"
          title="Move left"
        >
          <ChevronUp size={12} className="rotate-[-90deg]" />
        </button>
        <GripVertical
          size={14}
          className="text-white/15 cursor-grab active:cursor-grabbing"
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMove(index, 1);
          }}
          disabled={index === total - 1}
          className="p-0.5 rounded hover:bg-white/10 text-white/20 hover:text-white/60 disabled:opacity-0 transition"
          title="Move right"
        >
          <ChevronDown size={12} className="rotate-[-90deg]" />
        </button>
      </div>

      {/* Top row: icon + status + LLM badge */}
      <div className="flex items-start justify-between mb-5">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center relative"
          style={{ background: `${color}15` }}
        >
          <Icon size={26} style={{ color }} />
          {/* Subtle glow behind icon */}
          {isActive && (
            <div
              className="absolute inset-0 rounded-2xl animate-glow-pulse"
              style={{ boxShadow: `0 0 20px ${color}20` }}
            />
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* LLM badge */}
          {agent.llmEnabled && (
            <span
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${
                llmOnline
                  ? "bg-purple-500/10 border-purple-500/30 text-purple-400"
                  : "bg-white/5 border-white/15 text-white/65"
              }`}
              title={
                llmOnline
                  ? "LLM-powered (Online)"
                  : "LLM-powered (Offline — API key needed)"
              }
            >
              <Brain size={11} />
              LLM
              {llmOnline ? (
                <Wifi size={9} className="text-green-400" />
              ) : (
                <WifiOff size={9} className="text-red-400/60" />
              )}
            </span>
          )}

          {isActive ? (
            <span
              className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
              style={{ background: `${color}15`, color }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: color }}
              />
              Active
            </span>
          ) : (
            <span className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-white/8 text-white/40 uppercase tracking-wider flex items-center gap-1.5 border border-white/10">
              <Lock size={10} />
              {isRestricted ? "Restricted" : "Coming Soon"}
            </span>
          )}
        </div>
      </div>

      {/* Title + category */}
      <h3
        className={`font-bold text-lg ${isActive ? "text-white" : "text-white/50"}`}
      >
        {agent.name}
      </h3>
      <span className="text-[11px] text-white/60 uppercase tracking-wider font-semibold">
        {agent.category}
      </span>

      {/* Description */}
      <p
        className={`text-sm mt-3 leading-relaxed ${isActive ? "text-white/75" : "text-white/45"}`}
      >
        {agent.description}
      </p>

      {/* Workflow steps */}
      <div className="flex flex-wrap items-center gap-1.5 mt-5">
        {agent.workflow.map((step, i) => (
          <span key={i} className="flex items-center gap-1">
            <span
              className="text-[11px] px-2.5 py-1 rounded-lg font-medium"
              style={{
                background: isActive ? `${color}10` : "rgba(255,255,255,0.03)",
                color: isActive ? `${color}` : "rgba(255,255,255,0.2)",
              }}
            >
              {step}
            </span>
            {i < agent.workflow.length - 1 && (
              <span className="text-white/25 text-[11px]">→</span>
            )}
          </span>
        ))}
      </div>

      {/* Output artifacts */}
      <div className="mt-5 pt-5 border-t border-white/5">
        <p className="text-[11px] text-white/55 uppercase tracking-wider mb-2 font-semibold">
          Outputs
        </p>
        <div className="flex flex-wrap gap-1.5">
          {agent.outputs.map((o) => (
            <span
              key={o.id}
              className="px-2 py-0.5 rounded text-[11px] font-medium"
              style={{
                background: isActive ? `${color}10` : "rgba(255,255,255,0.03)",
                color: isActive ? color : "rgba(255,255,255,0.2)",
              }}
            >
              {o.label}
            </span>
          ))}
        </div>
      </div>

      {/* CTA */}
      {isActive && (
        <div className="mt-6">
          <button
            className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all duration-300"
            style={{
              background: `linear-gradient(135deg, ${color}, ${color}CC)`,
              boxShadow: `0 0 20px ${color}20`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 0 30px ${color}40`;
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = `0 0 20px ${color}20`;
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Launch Agent
            <ArrowRight
              size={16}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </button>
        </div>
      )}
    </div>
  );
}
